import { colorInk, mixColor, type SerpMapStyle } from './serp-appearance.js';
import type { HeatmapResult, heatmapPalette } from './serp-heatmap.js';
import { queryTile } from './serp-treemap.js';
import { queryPositionMetrics } from './serp-tile-metrics.js';

export function queryTileSummary(status: string, results: HeatmapResult[], top: number) {
  return {
    ...queryTile(status, results, top),
    metrics: status === 'complete' ? queryPositionMetrics(results, top) : null,
  };
}
export type QueryTileSummary = ReturnType<typeof queryTileSummary>;

// Shared meaning and visual hierarchy for interactive tiles and frozen report images.
export function queryTilePresentation(
  status: string,
  results: HeatmapResult[],
  top: number,
  style: SerpMapStyle,
  palette: ReturnType<typeof heatmapPalette>,
) {
  return presentQueryTile(queryTileSummary(status, results, top), top, style, palette);
}

export function presentQueryTile(
  state: QueryTileSummary,
  top: number,
  style: SerpMapStyle,
  palette: ReturnType<typeof heatmapPalette>,
) {
  const metrics = state.metrics;
  const ownedCount = metrics?.owned.count ?? null;
  const review = metrics?.review.count ?? 0;
  const ownedLabel = ownedCount === null ? 'Нет полных данных' : `Наши: ${ownedCount} из ${top}`;
  const signalLabel =
    state.tone === 'unavailable'
      ? ''
      : state.tone === 'owned'
        ? review
          ? `Требуют разбора: ${review}`
          : 'Подтверждённых угроз не выявлено'
        : state.label;
  const risk = ['fake', 'conflict', 'competitor'].includes(state.tone);
  const base = mixColor(palette.text, palette.background, 0.09);
  let fill = base;
  if (style === 'soft') {
    // Unreviewed results remain a visible signal, without replacing ownership's
    // fill. Confirmed risks, conflicts and unknown states retain their treatment.
    const fillTone = state.tone === 'unreviewed' && ownedCount ? 'owned' : state.tone;
    const strength =
      fillTone === 'owned'
        ? 0.14 + 0.14 * (ownedCount! / top)
        : risk
          ? 0.2 + state.strength * 0.25
          : state.tone === 'unreviewed'
            ? 0.14
            : 0.06;
    fill = mixColor(palette[fillTone], base, strength);
  } else if (style === 'risk' && risk) {
    fill = mixColor(palette[state.tone], base, 0.12 + state.strength * 0.2);
  }
  const ink = colorInk(fill);
  return {
    ...state,
    fill,
    ink,
    // Signal colors follow the workspace palette; labels remain independent of color.
    accent: mixColor(palette[state.tone], ink, 0.7),
    ownedAccent: mixColor(palette.owned, ink, 0.7),
    track: mixColor(ink, fill, 0.16),
    ownedCount,
    metrics,
    ownedLabel,
    signalLabel,
    label: state.tone === 'unavailable' ? state.label : `${ownedLabel} · ${signalLabel}`,
    share: ownedCount === null ? null : ownedCount / top,
    hatch: ['unknown', 'unavailable'].includes(state.tone),
  };
}
